/**
 * Chat module state management — signal-based store.
 *
 * Session list, histories, message queue, WS sync. Per-chat streaming state
 * (content, seq, start time) lives in `chat-state.ts`. This lets multiple
 * chats stream concurrently without blocking each other.
 *
 * As of Phase 3, the chunk transport is the persistent WebSocket (no more
 * per-message SSE). User messages are sent via `sendChatMessage` on the
 * shared WS; the server streams chunks back stamped with `chat_id` + `seq`;
 * the stream router applies each chunk to its chat's state. On reconnect,
 * the client subscribes with last-seen seq per chat and the server replays
 * any missed chunks from its per-chat replay buffer.
 */

import { signal, computed, effect } from '@preact/signals';
import { route } from 'preact-router';
import { api } from '../../core/api/client';
import type { ChatHistoryPage, CodexModel, DurableTurn, EngineProfile } from '../../core/api/client';
import {
  onMessage,
  cancelQueuedChatMessages,
  sendChatMessage as wsSendChatMessage,
  sendSubscribe as wsSendSubscribe,
  forceReconnect,
  wsConnected,
} from '../../core/ws/client';
import type { ChatSession, ChatMessage } from '../../core/api/types';
import { NEW_ROOM_TITLE, markRoomEngine } from './study-room-state';
import { privateUnlocked } from '../../core/lock/lock-state';
import {
  chatStreamStates,
  getChatStream,
  updateChatStream,
  clearChatStream,
  setSnapshotHook,
  type ChatStreamState,
} from './chat-state';
import {
  routeChunk,
  setAssistantAppender,
  setStreamFinalized,
  beginStream,
} from './chat-stream-router';
import {
  snapshotStream,
  cancelPendingSnapshot,
  clearStream as clearStreamIdb,
  persistHistory,
  loadHistories,
  loadStreamSnapshots,
  clearHistory as clearHistoryIdb,
} from './chat-persist';
import { handlePromptEvent, loadChatPrompts } from './chat-prompts';

// ============================================================
// Signals — session / UI state (not stream state)
// ============================================================

/** List of all chat sessions, most-recent first */
export const chats = signal<ChatSession[]>([]);
export const chatsLoading = signal(false);
export const chatsLoadError = signal<string | null>(null);
let chatsLoadInFlight: Promise<void> | null = null;
let metadataRead: Promise<void> | null = null;
let lastMetadataReadAt = 0;
interface HistoryPageState {
  beforeId: string | null;
  hasMore: boolean;
  loadingOlder: boolean;
  expanded: boolean;
  error: string | null;
}
export const historyPages = signal<Record<number, HistoryPageState>>({});
const historyReads = new Map<number, Promise<ChatHistoryPage>>();
const historyRecency = new Map<number, number>();
const historyPaintCaches = new WeakSet<ChatMessage[]>();

function touchHistory(chatId: number): void {
  historyRecency.set(chatId, Date.now());
  const idle = Object.keys(chatHistories.value).map(Number).filter(id =>
    id !== activeChatId.value && id !== chatId && !getChatStream(id).streaming
    && !getChatStream(id).interrupted && !remoteStreamingChats.value[id]
    && !pendingQueue.value.some(message => message.chatId === id),
  ).sort((a, b) => (historyRecency.get(b) ?? 0) - (historyRecency.get(a) ?? 0));
  if (idle.length <= 4) return;
  const histories = { ...chatHistories.value };
  const pages = { ...historyPages.value };
  for (const id of idle.slice(4)) {
    delete histories[id];
    delete pages[id];
    historyRecency.delete(id);
  }
  chatHistories.value = histories;
  historyPages.value = pages;
}

async function hydrateChatHistory(chatId: number): Promise<void> {
  if (chatHistories.value[chatId] || !canReceiveChatWhileLocked(chatId)) return;
  const epoch = privateHistoryEpoch;
  try {
    const histories = await loadHistories([chatId]);
    if (!chatHistories.value[chatId] && histories[chatId]
      && epoch === privateHistoryEpoch && canReceiveChatWhileLocked(chatId)) {
      const cached = histories[chatId].slice(-30);
      historyPaintCaches.add(cached);
      chatHistories.value = { ...chatHistories.value, [chatId]: cached };
      touchHistory(chatId);
    }
  } catch { /* The server remains authoritative when the paint cache is unavailable. */ }
}
let privateHistoryEpoch = 0;
privateUnlocked.subscribe(() => { privateHistoryEpoch += 1; });

export function isPrivateChatId(chatId: number): boolean {
  return chats.value.some((chat) => chat.id === chatId && chat.private === true);
}

function canReceiveChatWhileLocked(chatId: number): boolean {
  if (privateUnlocked.value) return true;
  const chat = chats.value.find((candidate) => candidate.id === chatId);
  return chat?.private === false;
}

/** localStorage key for the most-recently-active chat (MRU restore on app load). */
const ACTIVE_CHAT_KEY = 'jarvis.activeChatId';
const CHAT_SPLIT_MODE_KEY = 'jarvis.chatSplitMode';

/** Read the last-active chat id persisted by a previous session, if any. */
function loadPersistedActiveChatId(): number {
  try {
    // A notification deep link (`/?chat=<id>`, PWA PH-02) wins over the MRU tab.
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('chat');
    if (linked != null) {
      // Used once: a later reload must restore the tab Joseph moved to, not the link.
      params.delete('chat');
      const query = params.toString();
      window.history.replaceState(
        window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    }
    if (linked != null && /^-?\d+$/.test(linked)) return parseInt(linked, 10);
  } catch {
    /* no window (SSR / tests) */
  }
  try {
    const raw = localStorage.getItem(ACTIVE_CHAT_KEY);
    if (raw != null) {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch {
    /* localStorage unavailable (SSR / tests); fall through to default */
  }
  return -10001;
}

function loadPersistedChatSplitMode(): boolean {
  try {
    return localStorage.getItem(CHAT_SPLIT_MODE_KEY) === '1';
  } catch {
    /* localStorage unavailable (SSR / tests); fall through to default */
  }
  return false;
}

/** Currently active chat ID. Seeded from localStorage so the app reopens the
 *  tab Joseph was last on (MRU); -10001 = default/ephemeral when none stored. */
export const activeChatId = signal<number>(loadPersistedActiveChatId());

/** Persist the active chat on every change so the next load restores it (MRU). */
effect(() => {
  const id = activeChatId.value;
  try {
    localStorage.setItem(ACTIVE_CHAT_KEY, String(id));
  } catch {
    /* ignore persistence failures */
  }
});

/** Per-chat message histories keyed by chat ID */
export const chatHistories = signal<Record<number, ChatMessage[]>>({});

/** Mobile: show chat list overlay instead of the chat view */
export const showMobileList = signal(false);

/** Fold/tablet single-canvas toggle: show chat list beside the active chat. */
export const chatSplitMode = signal(loadPersistedChatSplitMode());

effect(() => {
  try {
    localStorage.setItem(CHAT_SPLIT_MODE_KEY, chatSplitMode.value ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
});

/** Which thread group the sidebar shows. */
export type ChatGroup = 'business' | 'study' | 'reflections' | 'coding' | 'fantasy';
const CHAT_FILTER_KEY = 'jarvis.chatFilter';
const CHAT_GROUPS: readonly ChatGroup[] = ['business', 'study', 'reflections', 'coding', 'fantasy'];
/**
 * Marks a Study tab, listed under Rooms (memory/systems/study-rooms.md). The server attaches the Study
 * agent when it marks the tab; the agent binds the tab to a subject and names it.
 */
export const STUDY_ROOM_PROFILE = 'study-room';
function loadPersistedChatFilter(): ChatGroup {
  try {
    const saved = localStorage.getItem(CHAT_FILTER_KEY) as ChatGroup | null;
    return saved && CHAT_GROUPS.includes(saved) ? saved : 'business';
  } catch {
    return 'business';
  }
}
export const chatFilter = signal<ChatGroup>(loadPersistedChatFilter());

/**
 * Single source of truth for which group a chat belongs to. The groups are mutually
 * exclusive and exhaustive: an overlay-profile tab must NOT also appear under Business,
 * or the same thread shows up twice and switching groups looks broken.
 */
export function chatGroupOf(chat: { private?: boolean; profile?: string | null }): ChatGroup {
  // A PIN-locked chat lives in Reflections, a locked study room included: that group is what keeps
  // its name off the screen and its messages behind the PIN.
  if (chat.private) return 'reflections';
  if (chat.profile === STUDY_ROOM_PROFILE) return 'study';
  if (chat.profile === 'coding') return 'coding';
  if (chat.profile === 'fantasy-football') return 'fantasy';
  return 'business';
}

/** Chats in the given group, in the order the API returned them. */
export function chatsInGroup(group: ChatGroup): ChatSession[] {
  return chats.value.filter((c) => chatGroupOf(c) === group);
}

/**
 * Activate a known chat and keep the visible sidebar group aligned with it.
 * Every activation path goes through this helper so a direct switch, reload,
 * creation, deletion fallback, or swipe cannot leave a hidden active chat in
 * a different group.
 */
function activateKnownChat(chat: ChatSession): void {
  chatFilter.value = chatGroupOf(chat);
  activeChatId.value = chat.id;
}

/** Chats adjacent to the active chat within its exclusive sidebar group. */
export function activeGroupChats(): ChatSession[] {
  const active = chats.value.find((chat) => chat.id === activeChatId.value);
  return chatsInGroup(active ? chatGroupOf(active) : chatFilter.value);
}

/** One-based mobile position inside the active chat's group. */
export function activeGroupPosition(): { index: number; total: number } | null {
  const group = activeGroupChats();
  const index = group.findIndex((chat) => chat.id === activeChatId.value);
  return index >= 0 ? { index: index + 1, total: group.length } : null;
}

/** Neighbor for a mobile swipe, constrained to the active chat's group. */
export function adjacentChatId(direction: 'next' | 'prev'): number | null {
  const group = activeGroupChats();
  const index = group.findIndex((chat) => chat.id === activeChatId.value);
  if (index < 0) return null;
  const targetIndex = direction === 'next' ? index + 1 : index - 1;
  return group[targetIndex]?.id ?? null;
}

effect(() => {
  try {
    localStorage.setItem(CHAT_FILTER_KEY, chatFilter.value);
  } catch {
    /* ignore persistence failures */
  }
});

/** Per-chat remote-streaming indicator (sidebar dot for chats that are
 *  streaming on the server but don't have a local SSE reader). */
export const remoteStreamingChats = signal<Record<number, boolean>>({});

/**
 * Render live progress as one compact prose block, like a Claude tab.
 * Codex sends a short "**Step heading**" summary per step; consecutive
 * headings fold into a single italic line instead of one paragraph each.
 */
export function composeProgress(blocks: Array<{ kind: string; text: string }>): string {
  const parts: string[] = [];
  let steps: string[] = [];
  const flush = () => {
    if (steps.length) parts.push(`*${steps.join(' · ')}*`);
    steps = [];
  };
  for (const block of blocks) {
    const heading = block.kind === 'summary' && !block.text.includes('\n')
      ? block.text.replace(/\*+/g, '').trim()
      : '';
    if (heading) {
      steps.push(heading);
    } else {
      flush();
      parts.push(block.text);
    }
  }
  flush();
  return parts.join('\n\n');
}

/** Messages queued while a chat's own stream is in progress */
export interface QueuedMessage {
  id?: string;
  content: string;
  attachments: Array<{ type: string; name: string; url?: string }>;
  /** Chat ID captured at queue time — ensures correct routing if the user
   *  switches tabs before the queue drains. */
  chatId: number;
  /** True once the payload has actually been handed to the gateway. Such a row
   *  is awaiting only its `accepted` ack; the gateway owns it from here and it
   *  must never be re-sent or counted as waiting work. */
  sent?: boolean;
  /** PH-02: start this turn in plan mode. */
  planFirst?: boolean;
  /** Epoch ms the row entered the queue. Drives the orphan sweep below. */
  queuedAt?: number;
}
/** v2: v1 rows could not distinguish sent-from-unsent, so any orphan in a v1
 *  store is unclassifiable. Bumping the key discards them once. */
const PENDING_QUEUE_KEY = 'jarvis.pendingChatQueue.v2';
/** A sent row whose ack never arrived is an orphan. Past this age it can no
 *  longer belong to a live turn, so drop it rather than display it forever. */
const SENT_ORPHAN_TTL_MS = 10 * 60 * 1000;

function loadPendingQueue(): QueuedMessage[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_QUEUE_KEY) || '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((item): item is QueuedMessage => {
      if (!item || typeof item !== 'object') return false;
      const candidate = item as Partial<QueuedMessage>;
      return typeof candidate.content === 'string'
        && typeof candidate.chatId === 'number'
        && Number.isFinite(candidate.chatId)
        && Array.isArray(candidate.attachments);
    }).filter((item) => {
      // Drop stale sent-but-unacked rows: the turn they belonged to is long over.
      if (!item.sent) return true;
      const age = now - (typeof item.queuedAt === 'number' ? item.queuedAt : 0);
      return age < SENT_ORPHAN_TTL_MS;
    }).map((item) => ({
      ...item,
      id: item.id || crypto.randomUUID(),
      queuedAt: typeof item.queuedAt === 'number' ? item.queuedAt : now,
    }));
  } catch {
    return [];
  }
}

/** Flag a queued row as handed to the gateway. Idempotent. */
function markQueuedMessageSent(id: string | undefined): void {
  if (!id) return;
  pendingQueue.value = pendingQueue.value.map(
    (message) => (message.id === id ? { ...message, sent: true } : message),
  );
}

/** Rows genuinely waiting on this client to transmit them. Excludes rows the
 *  gateway already has, which it queues and drains on its own side. */
export function unsentQueuedCount(chatId: number): number {
  return pendingQueue.value.filter(
    (message) => message.chatId === chatId && !message.sent,
  ).length;
}

export const pendingQueue = signal<QueuedMessage[]>(loadPendingQueue());

effect(() => {
  try {
    localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(pendingQueue.value));
  } catch {
    // Storage can be unavailable in private mode. In-memory queuing still works.
  }
});

/** Files currently pending upload / ready to send */
export interface PendingAttachment {
  id: string;
  file: File;
  name: string;
  uploading: boolean;
  url?: string;
  type?: string;
  thumbUrl?: string;
  error?: string;
}
export const pendingAttachments = signal<PendingAttachment[]>([]);

/** Number of files currently uploading */
export const uploadingCount = signal(0);

/** Last attachment-upload error, surfaced to ChatInput banner */
export const attachmentUploadError = signal<string>('');

/** Current think/effort level for the active chat.
 *  Maps to Claude Code effort: low/medium/high/xhigh/max are model effort levels; 'ultracode'
 *  is the new workflow-orchestration mode (gateway applies it via --settings, not
 *  --effort). Default is 'high'; each chat can still override it with the chip. */
export type ThinkLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max' | 'ultracode';
export const THINK_LEVELS: ThinkLevel[] = ['low', 'medium', 'high', 'xhigh', 'max', 'ultracode'];
export const THINK_LABELS: Record<ThinkLevel, string> = { low: 'Low', medium: 'Medium', high: 'High', xhigh: 'X-High', max: 'Max', ultracode: 'Ultra' };
export const claudeEffort = signal<ThinkLevel>('high');

export type ClaudeModel = 'opus' | 'fable';
export const CLAUDE_MODELS: ClaudeModel[] = ['opus', 'fable'];
export const CLAUDE_MODEL_LABELS: Record<ClaudeModel, string> = { opus: 'Opus 5.5', fable: 'Fable 5.1' };
export const MODEL_VALUES: Record<ClaudeModel, string> = {
  opus: 'claude-opus-5-5[1m]',
  fable: 'claude-fable-5-1',
};
export const claudeModel = signal<ClaudeModel>('opus');

export function claudeModelFromServer(model: string | undefined | null): ClaudeModel {
  return model && model.toLowerCase().includes('fable') ? 'fable' : 'opus';
}

export type Provider = 'claude' | 'codex';
export const PROVIDERS: Provider[] = ['claude', 'codex'];
export const PROVIDER_LABELS: Record<Provider, string> = { claude: 'Claude', codex: 'Codex' };
export const provider = signal<Provider>('codex');

export const codexModel = signal('gpt-6.1-sol');
export const codexEffort = signal('high');
export const codexFast = signal(false);

const STATIC_CODEX_CATALOG: CodexModel[] = [
  {
    slug: 'gpt-6-astra',
    label: 'GPT-6 Astra',
    description: 'Model for complex, demanding work.',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'],
    default_effort: 'medium',
    supports_fast: true,
    priority: -1,
  },
  {
    slug: 'gpt-6.1-sol',
    label: 'GPT-6.1 Sol',
    description: 'Reliable agentic workhorse for everyday tasks.',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'],
    default_effort: 'medium',
    supports_fast: true,
    priority: 0,
  },
  {
    slug: 'gpt-6-luna',
    label: 'GPT-6 Luna',
    description: 'Fast and affordable agentic coding model.',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    default_effort: 'medium',
    supports_fast: true,
    priority: 1,
  },
  {
    slug: 'gpt-5.5',
    label: 'GPT-5.5',
    description: 'Frontier model for coding, research, and real-world work.',
    efforts: ['low', 'medium', 'high', 'xhigh'],
    default_effort: 'medium',
    supports_fast: true,
    priority: 3,
  },
  {
    slug: 'gpt-5.4',
    label: 'GPT-5.4',
    description: 'Strong model for everyday coding.',
    efforts: ['low', 'medium', 'high', 'xhigh'],
    default_effort: 'medium',
    supports_fast: true,
    priority: 4,
  },
  {
    slug: 'gpt-5.4-mini',
    label: 'GPT-5.4 Mini',
    description: 'Small model for simpler coding tasks.',
    efforts: ['low', 'medium', 'high', 'xhigh'],
    default_effort: 'medium',
    supports_fast: false,
    priority: 5,
  },
  {
    slug: 'gpt-5.3-codex-spark',
    label: 'GPT-5.3 Codex Spark',
    description: 'Fast coding model.',
    efforts: ['low', 'medium', 'high', 'xhigh'],
    default_effort: 'high',
    supports_fast: false,
    priority: 6,
  },
];

export const codexCatalog = signal<CodexModel[]>(STATIC_CODEX_CATALOG);
let codexCatalogRequest: Promise<CodexModel[]> | null = null;

export function loadCodexCatalog(): Promise<CodexModel[]> {
  if (!codexCatalogRequest) {
    codexCatalogRequest = api.getCodexModels()
      .then((response) => {
        if (response.models.length > 0) codexCatalog.value = response.models;
        return codexCatalog.value;
      })
      .catch(() => codexCatalog.value);
  }
  return codexCatalogRequest;
}

export function applyEngineProfile(profile: EngineProfile): void {
  provider.value = profile.provider === 'codex' ? 'codex' : 'claude';
  claudeModel.value = claudeModelFromServer(profile.claude?.model);
  const nextClaudeEffort = profile.claude?.effort as ThinkLevel | undefined;
  if (nextClaudeEffort && THINK_LEVELS.includes(nextClaudeEffort)) {
    claudeEffort.value = nextClaudeEffort;
  }
  if (profile.codex?.model) {
    codexModel.value = profile.codex.model === 'gpt-6-sol'
      ? 'gpt-6.1-sol'
      : profile.codex.model;
  }
  if (profile.codex?.effort) codexEffort.value = profile.codex.effort;
  codexFast.value = profile.codex?.fast === true;
}

/* Per-chat mutation epoch. Hydration snapshots this before reading and must not
 * apply an older profile after the user has changed a response setting. */
const engineProfileRevisions = new Map<number, number>();
export const activeProfileMutations = signal<Record<number, number>>({});

function waitForEngineProfileMutation(chatId: number): Promise<void> {
  if (activeProfileMutations.value[chatId] === undefined) return Promise.resolve();
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    unsubscribe = activeProfileMutations.subscribe((active) => {
      if (active[chatId] !== undefined) return;
      unsubscribe();
      resolve();
    });
  });
}

function drainPrivateQueueAfterUnlock(chatId: number): void {
  let unsubscribe = () => {};
  unsubscribe = privateUnlocked.subscribe((unlocked) => {
    if (!unlocked) return;
    unsubscribe();
    if (!getChatStream(chatId).streaming) drainQueue(chatId);
  });
}

export function getEngineProfileRevision(chatId: number): number {
  return engineProfileRevisions.get(chatId) ?? 0;
}

export function bumpEngineProfileRevision(chatId: number): number {
  const next = getEngineProfileRevision(chatId) + 1;
  engineProfileRevisions.set(chatId, next);
  return next;
}

/** Whether the shared terminal drawer is open. */
export const terminalDrawerOpen = signal(false);

/**
 * One-shot signal for the mobile swipe animation — see ChatView.
 */
export type SwipeSlideState = { dir: 'left' | 'right'; seq: number } | null;
export const swipeSlideDirection = signal<SwipeSlideState>(null);

// Re-export per-chat stream primitives for components that render them.
export { chatStreamStates, getChatStream };

// ============================================================
// Derived / computed
// ============================================================

/** Messages for the currently active chat */
export const activeMessages = computed<ChatMessage[]>(() => {
  return chatHistories.value[activeChatId.value] ?? [];
});

/** The active ChatSession object */
export const activeChat = computed<ChatSession | undefined>(() => {
  return chats.value.find((c) => c.id === activeChatId.value);
});

/** Streaming state for the active chat (empty state when not streaming). */
export const activeChatStream = computed(() => {
  return chatStreamStates.value[activeChatId.value];
});

// ============================================================
// Helpers — history mutation
// ============================================================

function appendUserMessage(
  chatId: number,
  content: string,
  attachments: Array<{ type: string; name: string; url?: string }>,
): void {
  const userMsg: ChatMessage = {
    role: 'user',
    content:
      content +
      (attachments.length > 0
        ? `\n[${attachments.length} file${attachments.length > 1 ? 's' : ''} attached]`
        : ''),
    timestamp: new Date().toISOString(),
    attachments: attachments.length > 0 ? attachments : undefined,
  };
  const nextMessages = [...(chatHistories.value[chatId] ?? []), userMsg];
  chatHistories.value = {
    ...chatHistories.value,
    [chatId]: nextMessages,
  };
  persistHistory(chatId, nextMessages).catch(() => {});
  chats.value = chats.value.map(chat => chat.id === chatId && chat.title_pending
    ? { ...chat, message_count: chat.message_count + 1 } : chat);
}

function appendAssistantMessage(chatId: number, content: string): void {
  if (!content) return;
  const msg: ChatMessage = {
    role: 'assistant',
    content,
    timestamp: new Date().toISOString(),
  };
  const nextMessages = [...(chatHistories.value[chatId] ?? []), msg];
  chatHistories.value = {
    ...chatHistories.value,
    [chatId]: nextMessages,
  };
  chats.value = chats.value.map((c) =>
    c.id === chatId ? { ...c, updated_at: new Date().toISOString() } : c,
  );
  persistHistory(chatId, nextMessages).catch(() => {});
}

// Wire the router's finalize path into our history.
setAssistantAppender(appendAssistantMessage);

// Install the IDB snapshot hook so every chunk that advances lastSeq fires a
// debounced persist. The hook itself is debounced per-chat inside chat-persist.
setSnapshotHook(snapshotStream);

// ============================================================
// Session actions
// ============================================================

let titleMetadataRead: Promise<void> | null = null;
export function refreshPendingChatTitles(): Promise<void> {
  if (titleMetadataRead) return titleMetadataRead;
  const before = new Map(chats.value.map(chat => [chat.id, chat]));
  titleMetadataRead = (async () => {
    try {
      const response = await api.getChats();
      const server = new Map(response.chats.map(chat => [chat.id, chat]));
      chats.value = chats.value.map(chat => {
        const fresh = server.get(chat.id);
        if (!fresh || chat.private || !chat.title_pending || before.get(chat.id) !== chat) return chat;
        return { ...chat, title: fresh.title, title_pending: fresh.title_pending,
          message_count: Math.max(chat.message_count, fresh.message_count) };
      });
    } catch { /* A later visible refresh retries without disturbing the conversation. */ }
    finally { titleMetadataRead = null; }
  })();
  return titleMetadataRead;
}

/** Only unnamed, nonempty chats cause polling, and only while this view is visible. */
export function startPendingTitleRefresh(): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let hasPending = false;
  let attempts = 0;
  let pendingSignature = '';
  const schedule = () => {
    if (stopped || !hasPending || attempts >= 10 || document.visibilityState !== 'visible' || timer) return;
    timer = setTimeout(async () => {
      timer = null;
      attempts += 1;
      await refreshPendingChatTitles();
      schedule();
    }, 3000);
  };
  const stopEffect = effect(() => {
    const pending = chats.value.filter(chat => chat.title_pending && !chat.private && chat.message_count > 0);
    const signature = pending.map(chat => `${chat.id}:${chat.message_count}`).join(',');
    if (signature !== pendingSignature) { attempts = 0; pendingSignature = signature; }
    hasPending = pending.length > 0;
    if (!hasPending && timer) { clearTimeout(timer); timer = null; }
    schedule();
  });
  const visibility = () => {
    if (document.visibilityState !== 'visible' && timer) { clearTimeout(timer); timer = null; }
    schedule();
  };
  document.addEventListener('visibilitychange', visibility);
  return () => {
    stopped = true;
    stopEffect();
    if (timer) clearTimeout(timer);
    document.removeEventListener('visibilitychange', visibility);
  };
}

/**
 * Fetch the chat list from the server and pick an active chat.
 * If no chats exist, creates a default "Main Chat".
 */
export function loadChats(): Promise<void> {
  if (chatsLoadInFlight) return chatsLoadInFlight;
  chatsLoading.value = true;
  chatsLoadError.value = null;
  const before = new Map(chats.value.map(chat => [chat.id, chat]));
  chatsLoadInFlight = (async () => {
    try {
      const data = await api.getChats();
      if (!data || !Array.isArray(data.chats) || !data.chats.every((chat) =>
        chat && Number.isSafeInteger(chat.id)
      )) throw new Error('Invalid chat list');
      const list = mergeChatMetadata(data.chats, before);
      lastMetadataReadAt = Date.now();

      if (list.length === 0) {
        if (await createChat('Main Chat') == null) throw new Error('Could not create chat');
        return;
      }

      chats.value = list;
      if (wsConnected.value) sendReplaySubscriptions();

      const restored = list.find((chat) => chat.id === activeChatId.value);
      const selectedGroupFallback = list.find((chat) => chatGroupOf(chat) === chatFilter.value);
      activateKnownChat(restored ?? selectedGroupFallback ?? list[0]);

      // IndexedDB is a paint cache only. Refresh the active server history.
      void hydrateChatHistory(activeChatId.value);
      await loadChatHistory(activeChatId.value);
    } catch (e) {
      chatsLoadError.value = 'Could not load chats. Please try again.';
      console.warn('[chat-store] loadChats failed:', e);
    } finally {
      chatsLoading.value = false;
      chatsLoadInFlight = null;
    }
  })();
  return chatsLoadInFlight;
}

/** Preserve local creations, renames and deletions newer than this read. */
function mergeChatMetadata(list: ChatSession[], before: Map<number, ChatSession>): ChatSession[] {
  const current = new Map(chats.value.map(chat => [chat.id, chat]));
  const merged = list.filter(chat => !before.has(chat.id) || current.has(chat.id))
    .map(chat => {
      const local = current.get(chat.id);
      return local && local !== before.get(chat.id) ? local : chat;
    });
  const ids = new Set(merged.map(chat => chat.id));
  return [...chats.value.filter(chat => !before.has(chat.id) && !ids.has(chat.id)), ...merged];
}

/** Read sidebar metadata without reloading every history or changing a valid selection. */
export function refreshChatMetadata(): Promise<void> {
  if (chatsLoadInFlight) return chatsLoadInFlight;
  if (metadataRead) return metadataRead;
  const before = new Map(chats.value.map(chat => [chat.id, chat]));
  metadataRead = (async () => {
    try {
      const response = await api.getChats();
      if (!Array.isArray(response?.chats) || !response.chats.every(chat => Number.isSafeInteger(chat?.id))) return;
      chats.value = mergeChatMetadata(response.chats, before);
      lastMetadataReadAt = Date.now();
      if (wsConnected.value) sendReplaySubscriptions();
      if (chats.value.length && !chats.value.some(chat => chat.id === activeChatId.value)) {
        activateKnownChat(chats.value.find(chat => chatGroupOf(chat) === chatFilter.value) ?? chats.value[0]);
        await loadChatHistory(activeChatId.value);
      }
    } catch { /* Retry on the next visible reconciliation without hiding usable tabs. */ }
  })().finally(() => { metadataRead = null; });
  return metadataRead;
}

/** The gateway writes each final answer under UUIDv5(URL, "pwa-turn:<turn id>"). */
async function canonicalAnswerId(turnId: string): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const namespace = '6ba7b8119dad11d180b400c04fd430c8';
  const name = new TextEncoder().encode(`pwa-turn:${turnId}`);
  const input = new Uint8Array(16 + name.length);
  for (let i = 0; i < 16; i++) input[i] = parseInt(namespace.slice(i * 2, i * 2 + 2), 16);
  input.set(name, 16);
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-1', input)).slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Load message history for a specific chat from the server.
 *
 * Writes to `chatHistories` but does NOT persist to IDB — the server is
 * authoritative at fetch time, but a transient truncation or empty response
 * would silently overwrite a richer local record (audit P2 #2). IDB stays
 * owned by client-initiated mutations (appendUserMessage, appendAssistant).
 */
export async function loadChatHistory(
  chatId: number,
  canCommit: () => boolean = () => true,
): Promise<boolean> {
  if (!privateUnlocked.value && isPrivateChatId(chatId)) return false;
  const historyBefore = chatHistories.value[chatId];
  const streamBefore = getChatStream(chatId);
  const privacyEpoch = privateHistoryEpoch;
  const wasPrivate = isPrivateChatId(chatId);
  try {
    let request = historyReads.get(chatId);
    if (!request) {
      request = api.getChatHistory(chatId);
      historyReads.set(chatId, request);
      const release = () => { if (historyReads.get(chatId) === request) historyReads.delete(chatId); };
      void request.then(release, release);
    }
    const data = await request;
    const streamNow = getChatStream(chatId);
    if (!canCommit()
      || ((wasPrivate || isPrivateChatId(chatId))
        && (!privateUnlocked.value || privacyEpoch !== privateHistoryEpoch))
      || (chatHistories.value[chatId] !== historyBefore && !historyPaintCaches.has(chatHistories.value[chatId]))
      || streamNow.streaming !== streamBefore.streaming
      || streamNow.content !== streamBefore.content
      || streamNow.lastSeq !== streamBefore.lastSeq
      || streamNow.turnId !== streamBefore.turnId
      || streamNow.startedAt !== streamBefore.startedAt) return false;
    let messages = data.messages ?? [];
    const page = historyPages.value[chatId];
    const overlap = page?.expanded && messages[0]?.id
      ? (historyBefore ?? []).findIndex(message => message.id === messages[0].id)
      : -1;
    if (overlap >= 0) messages = [...historyBefore!.slice(0, overlap), ...messages];
    chatHistories.value = {
      ...chatHistories.value,
      [chatId]: messages,
    };
    historyPages.value = { ...historyPages.value, [chatId]: overlap >= 0
      ? { ...page, error: null }
      : { beforeId: data.next_before_id ?? null, hasMore: data.has_more === true,
        loadingOlder: false, expanded: false, error: null } };
    if (!streamNow.streaming && streamNow.interruptionKind === 'transport' && streamNow.turnId) {
      const answerId = await canonicalAnswerId(streamNow.turnId);
      const current = getChatStream(chatId);
      if (answerId && messages.some(message => message.role === 'assistant' && message.id === answerId)
        && current.turnId === streamNow.turnId && current.startedAt === streamNow.startedAt
        && !current.streaming && current.interruptionKind === 'transport') {
        clearChatStream(chatId);
        void clearStreamIdb(chatId).catch(() => {});
        drainQueue(chatId);
      }
    }
    touchHistory(chatId);
    return true;
  } catch (e) {
    historyPages.value = { ...historyPages.value, [chatId]: {
      ...(historyPages.value[chatId] ?? {
        beforeId: null, hasMore: false, loadingOlder: false, expanded: false,
      }), error: 'Could not load messages. Please try again.',
    } };
    console.warn('[chat-store] loadChatHistory failed:', e);
    return false;
  }
}

/** Fetch older history only on request. Keep the visible page on network failure. */
export async function loadOlderChatHistory(chatId: number): Promise<boolean> {
  const page = historyPages.value[chatId];
  if (!page?.hasMore || !page.beforeId || page.loadingOlder
    || (!privateUnlocked.value && isPrivateChatId(chatId))) return false;
  const epoch = privateHistoryEpoch;
  historyPages.value = { ...historyPages.value, [chatId]: { ...page, loadingOlder: true, error: null } };
  try {
    const data = await api.getChatHistory(chatId, page.beforeId);
    if (epoch !== privateHistoryEpoch || (!privateUnlocked.value && isPrivateChatId(chatId))
      || historyPages.value[chatId]?.beforeId !== page.beforeId) return false;
    const current = chatHistories.value[chatId] ?? [];
    const ids = new Set(current.map(message => message.id).filter(Boolean));
    chatHistories.value = { ...chatHistories.value,
      [chatId]: [...data.messages.filter(message => !message.id || !ids.has(message.id)), ...current] };
    historyPages.value = { ...historyPages.value, [chatId]: {
      beforeId: data.next_before_id ?? null, hasMore: data.has_more === true,
      loadingOlder: false, expanded: true, error: null,
    } };
    touchHistory(chatId);
    return true;
  } catch {
    if (historyPages.value[chatId]?.beforeId === page.beforeId) {
      historyPages.value = { ...historyPages.value, [chatId]: {
        ...page, loadingOlder: false, error: 'Could not load older messages. Please try again.',
      } };
    }
    return false;
  } finally {
    const current = historyPages.value[chatId];
    if (current?.beforeId === page.beforeId && current.loadingOlder) {
      historyPages.value = { ...historyPages.value, [chatId]: { ...current, loadingOlder: false } };
    }
  }
}

/** Refresh only the active known chat. Inactive tabs load on switch; durable
 * completed turns and WebSocket replay reconcile independently. */
let backgroundReloadInFlight: Promise<void> | null = null;
let backgroundReloadSkip = new Set<number>();

export function reloadAllChatHistories(skip?: Set<number>): Promise<void> {
  for (const id of skip ?? []) backgroundReloadSkip.add(id);
  if (backgroundReloadInFlight) return backgroundReloadInFlight;
  const chatId = activeChatId.value;
  const canReload = () => chats.value.some((chat) => chat.id === chatId)
    && !backgroundReloadSkip.has(chatId)
    && !getChatStream(chatId).streaming
    && !remoteStreamingChats.value[chatId];
  backgroundReloadInFlight = (async () => {
    if (canReload()) await loadChatHistory(chatId, canReload);
  })().finally(() => {
    backgroundReloadInFlight = null;
    backgroundReloadSkip = new Set();
  });
  return backgroundReloadInFlight;
}

// Keep one trailing refresh after reconnect bursts, and never overlap reads.
const RELOAD_COALESCE_MS = 8000;
let _lastReloadAt = -Infinity;
let _reloadTimer: ReturnType<typeof setTimeout> | null = null;
let _reloadInFlight = false;
let _reloadPending = false;
let _reloadSkip = new Set<number>();

function scheduleHistoryReload(): void {
  if (_reloadInFlight || _reloadTimer || !_reloadPending) return;
  const delay = Math.max(0, RELOAD_COALESCE_MS - (Date.now() - _lastReloadAt));
  if (delay > 0) {
    _reloadTimer = setTimeout(() => {
      _reloadTimer = null;
      scheduleHistoryReload();
    }, delay);
    return;
  }
  _reloadPending = false;
  _reloadInFlight = true;
  _lastReloadAt = Date.now();
  const skip = _reloadSkip;
  _reloadSkip = new Set();
  void reloadAllChatHistories(skip).finally(() => {
    _reloadInFlight = false;
    scheduleHistoryReload();
  });
}

export function reloadAllChatHistoriesCoalesced(skip?: Set<number>): void {
  // New orphan preservation also invalidates a read already in flight.
  for (const id of skip ?? []) {
    _reloadSkip.add(id);
    backgroundReloadSkip.add(id);
  }
  _reloadPending = true;
  scheduleHistoryReload();
}

/** Switch to a different chat. Loads history. */
export async function switchChat(chatId: number): Promise<void> {
  const target = chats.value.find((chat) => chat.id === chatId);
  if (!target) return;

  const alreadyActive = chatId === activeChatId.value;
  activateKnownChat(target);

  if (alreadyActive) {
    showMobileList.value = false;
    return;
  }

  showMobileList.value = false;

  void hydrateChatHistory(chatId);
  await loadChatHistory(chatId);
}

/** Create a new chat session. */
export async function createChat(title?: string, channel?: 'pwa' | 'signal'): Promise<number | null> {
  try {
    const data = await api.createChat(title || 'New Chat', channel);
    const newChat = data.chat;

    chats.value = [newChat, ...chats.value];
    chatHistories.value = { ...chatHistories.value, [newChat.id]: [] };
    activateKnownChat(newChat);

    showMobileList.value = false;
    return newChat.id;
  } catch (e) {
    console.warn('[chat-store] createChat failed:', e);
    return null;
  }
}

/**
 * Create a business tab pre-assigned to an overlay profile (default: coding).
 * Still a normal business tab, approval gate and all; it just gets the profile's doctrine
 * injected and its role pinned.
 */
export async function createProfileTab(profile = 'coding'): Promise<number | null> {
  try {
    const data = await api.createProfileTab(profile);
    const newChat = data.chat;

    chats.value = [newChat, ...chats.value];
    chatHistories.value = { ...chatHistories.value, [newChat.id]: [] };
    activateKnownChat(newChat);

    showMobileList.value = false;
    return newChat.id;
  } catch (e) {
    console.warn('[chat-store] createProfileTab failed:', e);
    return null;
  }
}

/** How long a new room waits for its engine to be set before it opens anyway. */
const STUDY_ROOM_ENGINE_WAIT_MS = 4000;
let studyRoomInFlight: Promise<number | null> | null = null;

/**
 * Open a new Study tab. Joseph asks about any subject; the Study agent picks up that subject's note
 * (or starts one) and names the tab after it.
 * A second call while one is being made returns the same room, so a double tap makes one.
 */
export function createStudyRoom(): Promise<number | null> {
  if (!studyRoomInFlight) {
    studyRoomInFlight = openStudyRoom().finally(() => {
      studyRoomInFlight = null;
    });
  }
  return studyRoomInFlight;
}

async function openStudyRoom(): Promise<number | null> {
  try {
    const data = await api.createProfileTab(STUDY_ROOM_PROFILE);
    // The server names a profile tab after its profile; the Study agent names it after its subject.
    // A new room tab is never PIN-locked, whatever the list held for that id before.
    const newChat: ChatSession = {
      ...data.chat, title: NEW_ROOM_TITLE, title_pending: false, profile: STUDY_ROOM_PROFILE, private: false,
    };
    // Not waited for: the tab opens under its working name either way.
    void Promise.resolve(api.renameChat(newChat.id, newChat.title)).catch((e) => {
      console.warn('[chat-store] createStudyRoom rename failed:', e);
    });
    // Founding a room is design work and runs on Opus, whatever the app's default engine is. Waited
    // for briefly, so the engine chip reads right at first look, but a slow server does not hold the tab.
    const engine = Promise.resolve(api.setEngineProfile(newChat.id, {
      provider: 'claude', claude_model: MODEL_VALUES.opus, claude_effort: 'high',
    })).then(
      (profile): EngineProfile | true => profile ?? true,
      (e): false => {
        console.warn('[chat-store] createStudyRoom engine not set:', e);
        return false;
      },
    );
    const settled = await Promise.race([
      engine,
      new Promise<'waiting'>((resolve) => { setTimeout(() => resolve('waiting'), STUDY_ROOM_ENGINE_WAIT_MS); }),
    ]);
    if (settled === 'waiting' || settled === false) {
      markRoomEngine(newChat.id, false);
      if (settled === 'waiting') {
        void engine.then((late) => {
          if (late === false) return;
          markRoomEngine(newChat.id, true);
          if (late !== true && activeChatId.value === newChat.id) applyEngineProfile(late);
        });
      }
    }

    // A list refresh may have brought the tab in while the calls above were out.
    chats.value = [newChat, ...chats.value.filter((chat) => chat.id !== newChat.id)];
    chatHistories.value = { ...chatHistories.value, [newChat.id]: chatHistories.value[newChat.id] ?? [] };
    activateKnownChat(newChat);

    showMobileList.value = false;
    return newChat.id;
  } catch (e) {
    console.warn('[chat-store] createStudyRoom failed:', e);
    return null;
  }
}

/** Create a private "Reflections" tab (confidant / spiritual-director identity, walled). */
export async function createReflectionsTab(): Promise<number | null> {
  try {
    const data = await api.createReflectionsTab();
    // The legacy creation response omits `private` even though the persisted
    // row is private. Stamp the invariant before activation so the new tab is
    // grouped and locked correctly without waiting for a list reload.
    const newChat: ChatSession = { ...data.chat, private: true };

    chats.value = [newChat, ...chats.value];
    chatHistories.value = { ...chatHistories.value, [newChat.id]: [] };
    activateKnownChat(newChat);

    showMobileList.value = false;
    return newChat.id;
  } catch (e) {
    console.warn('[chat-store] createReflectionsTab failed:', e);
    return null;
  }
}

/** Delete a chat session. Switches to the next available chat. */
export async function deleteChat(chatId: number): Promise<void> {
  if (chats.value.length <= 1) return;

  try {
    const deleted = chats.value.find((chat) => chat.id === chatId);
    const deletedGroup = deleted ? chatGroupOf(deleted) : null;
    const deletedGroupIndex = deletedGroup
      ? chatsInGroup(deletedGroup).findIndex((chat) => chat.id === chatId)
      : -1;
    await api.deleteChat(chatId);

    const remaining = chats.value.filter((c) => c.id !== chatId);
    const { [chatId]: _removed, ...rest } = chatHistories.value;
    chatHistories.value = rest;
    chats.value = remaining;

    // Clear any stream state for the deleted chat
    clearChatStream(chatId);
    clearStreamIdb(chatId).catch(() => {});
    clearHistoryIdb(chatId).catch(() => {});

    // Purge any queued messages that targeted the deleted chat
    const beforeCount = pendingQueue.value.length;
    pendingQueue.value = pendingQueue.value.filter((m) => m.chatId !== chatId);
    if (pendingQueue.value.length < beforeCount) {
      console.log(`[chat-store] Purged ${beforeCount - pendingQueue.value.length} queued message(s) for deleted chat ${chatId}`);
    }

    if (chatId === activeChatId.value && remaining.length > 0) {
      const sameGroup = deletedGroup
        ? remaining.filter((chat) => chatGroupOf(chat) === deletedGroup)
        : [];
      const next = sameGroup.length > 0
        ? sameGroup[Math.min(Math.max(deletedGroupIndex, 0), sameGroup.length - 1)]
        : remaining[0];
      activateKnownChat(next);
      if (!chatHistories.value[next.id]) {
        await loadChatHistory(next.id);
      }
    } else {
      const active = remaining.find((chat) => chat.id === activeChatId.value);
      if (active) activateKnownChat(active);
    }
  } catch (e) {
    console.warn('[chat-store] deleteChat failed:', e);
  }
}

/** Rename a chat session. */
export async function renameChat(chatId: number, title: string): Promise<void> {
  try {
    await api.renameChat(chatId, title);
    chats.value = chats.value.map((c) =>
      c.id === chatId ? { ...c, title, title_pending: false } : c,
    );
  } catch (e) {
    console.warn('[chat-store] renameChat failed:', e);
  }
}

// ============================================================
// sendMessage — WS-only (chunks arrive asynchronously via routeChunk)
// ============================================================

/**
 * Send a message to a chat over the persistent WS. Chunks stream back
 * multiplexed on the same WS and are routed to per-chat state by the
 * chat-stream-router (see `subscribeToChatWS` below).
 *
 * Each chat streams independently. Sending to chat B while chat A is still
 * streaming no longer blocks — chat A keeps going, chat B starts immediately.
 * Only sending to the SAME chat that is already streaming will queue.
 *
 * Returns a Promise that resolves as soon as the payload is enqueued (or
 * accepted by the WS). Completion of the stream is observed via state
 * signals, not the returned Promise.
 */
export async function sendMessage(
  content: string,
  attachments: Array<{ type: string; name: string; url?: string }> = [],
  targetChatId?: number,
  options?: { planFirst?: boolean },
): Promise<void> {
  if (!content.trim() && attachments.length === 0) return;

  const chatId = targetChatId ?? activeChatId.value;
  if (!privateUnlocked.value && isPrivateChatId(chatId)) return;
  const queuedMessage: QueuedMessage = {
    id: crypto.randomUUID(),
    content,
    attachments,
    chatId,
    queuedAt: Date.now(),
    ...(options?.planFirst ? { planFirst: true } : {}),
  };
  appendUserMessage(chatId, content, attachments);
  pendingQueue.value = [...pendingQueue.value, queuedMessage];

  if (activeProfileMutations.value[chatId] !== undefined) {
    await waitForEngineProfileMutation(chatId);
    const pendingMessage = pendingQueue.value.find((message) => message.id === queuedMessage.id);
    if (!pendingMessage || pendingMessage.sent) return;
    if (!privateUnlocked.value && isPrivateChatId(chatId)) {
      drainPrivateQueueAfterUnlock(chatId);
      return;
    }
  }

  // Queue only if THIS chat is streaming (per-chat semantics).
  if (getChatStream(chatId).streaming) {
    // The gateway keeps its own per-chat queue, so a message it accepted is
    // its responsibility to drain. Mark it sent so drainQueue never re-sends it.
    if (wsSendChatMessage(
      chatId, content, attachments, undefined, queuedMessage.id,
      ...(queuedMessage.planFirst ? [{ planFirst: true }] : []),
    )) {
      markQueuedMessageSent(queuedMessage.id);
    } else {
      // Transport refused it, so the ws client buffered the raw frame in its
      // own outbox. Two owners would each retry and produce a duplicate turn:
      // drop the transport copy and let pendingQueue be the single retrier.
      cancelQueuedChatMessages(chatId);
    }
    return;
  }

  startMessageTransport(queuedMessage);
}

function startMessageTransport(
  message: QueuedMessage,
): void {
  const { chatId, content, attachments } = message;
  if (!pendingQueue.value.some((queued) => queued.id === message.id)) {
    pendingQueue.value = [...pendingQueue.value, message];
  }

  // Before a fresh stream begins, drop any debounce still queued for this
  // chat from a prior stream — otherwise the stale content could land in
  // IDB after beginStream resets state, then hydrate on cold restart as
  // empty content (see audit C2).
  cancelPendingSnapshot(chatId);

  // No AbortController — WS-only flow. `abortStream` tells the server to
  // stop; the server's error/done response finalizes via routeChunk.
  beginStream(chatId, null);

  const sent = wsSendChatMessage(
    chatId, content, attachments, undefined, message.id,
    ...(message.planFirst ? [{ planFirst: true }] : []),
  );
  if (sent) markQueuedMessageSent(message.id);
  if (!sent) {
    cancelQueuedChatMessages(chatId);
    clearChatStream(chatId);
    clearStreamIdb(chatId).catch(() => {});
    if (!pendingQueue.value.some((queued) =>
      queued.id === message.id
      || queued.chatId === chatId
      && queued.content === content
      && JSON.stringify(queued.attachments) === JSON.stringify(attachments)
    )) {
      pendingQueue.value = [...pendingQueue.value, message];
    }
    console.warn(
      '[chat-store] Transport not ready, message kept in durable chat queue',
    );
  }
}

/**
 * User-initiated stream abort for a given chat (defaults to the active chat).
 *
 * Tells the server to SIGINT the LLM. The server's final error/done chunk
 * finalizes state via the router. A short safety timeout catches the case
 * where the server never replies (e.g., gateway wedged) and force-finalizes
 * locally so the UI never stays stuck on a dead stream.
 */
export function abortStream(chatId?: number): void {
  const targetId = chatId ?? activeChatId.value;
  const state = getChatStream(targetId);
  if (!state.streaming) return;
  const targetStartedAt = state.startedAt;

  console.log('[chat-store] User-initiated stream abort for chat', targetId);
  cancelQueuedChatMessages(targetId);
  pendingQueue.value = pendingQueue.value.filter((message) => message.chatId !== targetId);

  // Safety net: if no done/error arrives in 10s, finalize locally with
  // whatever content we have. drainQueue fires via setStreamFinalized hook.
  const fallbackTimer = setTimeout(() => {
    const st = getChatStream(targetId);
    if (!st.streaming || st.startedAt !== targetStartedAt) return;
    console.warn('[chat-store] Abort timeout — force-finalizing chat', targetId);
    finalizeBrowserLocalStream(targetId, targetStartedAt);
    setTimeout(() => loadChatHistory(targetId), 500);
  }, 10000);

  api.stopChat(targetId)
    .then((result) => {
      if (result.stopped !== 0) return;
      clearTimeout(fallbackTimer);
      finalizeBrowserLocalStream(targetId, targetStartedAt);
    })
    .catch(() => {
      // Keep the fallback armed. A real server stream may still terminate.
    });
}

function finalizeBrowserLocalStream(
  chatId: number,
  expectedStartedAt: number | null,
): void {
  const state = getChatStream(chatId);
  if (!state.streaming || state.startedAt !== expectedStartedAt) return;
  if (state.content) appendAssistantMessage(chatId, state.content);
  clearChatStream(chatId);
  clearStreamIdb(chatId).catch(() => {});
}

/**
 * Send the next queued message for a chat, if any. Called from the router's
 * finalize hook (`setStreamFinalized` below) and the abort safety-net path.
 */
function drainQueue(chatId: number): void {
  if (activeProfileMutations.value[chatId] !== undefined) {
    void waitForEngineProfileMutation(chatId).then(() => {
      if (!privateUnlocked.value && isPrivateChatId(chatId)) {
        drainPrivateQueueAfterUnlock(chatId);
        return;
      }
      const stream = getChatStream(chatId);
      if (!stream.streaming) drainQueue(chatId);
    });
    return;
  }
  // Only rows this client still owes the gateway are drainable. A row already
  // transmitted is awaiting its ack (or the gateway's own queue) and re-sending
  // it would duplicate the turn.
  const idx = pendingQueue.value.findIndex((m) => m.chatId === chatId && !m.sent);
  if (idx === -1) return;

  const next = pendingQueue.value[idx].id
    ? pendingQueue.value[idx]
    : { ...pendingQueue.value[idx], id: crypto.randomUUID() };
  pendingQueue.value = pendingQueue.value.filter((_, i) => i !== idx);

  try {
    startMessageTransport(next);
  } catch (err) {
    console.error('[chat-store] drainQueue sendMessage failed:', err);
    clearChatStream(chatId);
    if (!pendingQueue.value.some((message) => message.id === next.id)) {
      pendingQueue.value = [...pendingQueue.value, next];
    }
    setTimeout(() => drainQueue(chatId), 2000);
  }
}

// Wire the router's stream-finalized hook: drain the per-chat queue and free
// the IDB snapshot now that the stream is locked into history.
setStreamFinalized((chatId: number, turnId: string | null) => {
  clearStreamIdb(chatId).catch(() => {});
  if (remoteStreamingChats.value[chatId]) {
    const next = { ...remoteStreamingChats.value };
    delete next[chatId];
    remoteStreamingChats.value = next;
  }
  if (turnId) api.acknowledgeChatTurn(turnId).catch(() => {});
  // Any row for this chat that was transmitted has either been acked already
  // or never will be; the gateway drains its own queue independently. Clearing
  // them here keeps the persisted store from growing and makes a missed ack
  // self-heal within one turn instead of waiting out SENT_ORPHAN_TTL_MS.
  pendingQueue.value = pendingQueue.value.filter(
    (message) => !(message.chatId === chatId && message.sent),
  );
  drainQueue(chatId);
});

// ============================================================
// Attachment helpers (unchanged semantics)
// ============================================================

export async function uploadAttachment(file: File): Promise<void> {
  const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const isImage = file.type.startsWith('image/');
  const thumbUrl = isImage ? URL.createObjectURL(file) : undefined;

  const att: PendingAttachment = {
    id,
    file,
    name: file.name,
    uploading: true,
    thumbUrl,
  };

  console.log('[chat-store] uploadAttachment start:', {
    id,
    name: file.name,
    type: file.type || '(empty)',
    size: file.size,
    sizeMB: (file.size / (1024 * 1024)).toFixed(2),
    lastModified: file.lastModified,
  });

  pendingAttachments.value = [...pendingAttachments.value, att];
  uploadingCount.value++;

  try {
    const result = await api.uploadFile(file);

    console.log('[chat-store] uploadAttachment success:', { id, url: result.url, type: result.type });

    pendingAttachments.value = pendingAttachments.value.map((a) =>
      a.id === id
        ? { ...a, uploading: false, url: result.url, type: result.type }
        : a,
    );
  } catch (e) {
    const status = (e as { status?: number })?.status;
    const rawMsg = e instanceof Error ? e.message : String(e);
    const errMsg = status ? `${rawMsg} (HTTP ${status})` : rawMsg || 'network error (request did not reach server)';
    console.error('[chat-store] uploadAttachment failed:', {
      id,
      name: file.name,
      size: file.size,
      type: file.type,
      status,
      message: rawMsg,
      error: e,
    });
    pendingAttachments.value = pendingAttachments.value.map((a) =>
      a.id === id ? { ...a, uploading: false, error: errMsg } : a,
    );
    attachmentUploadError.value = `Upload failed for "${file.name}": ${errMsg}`;
  } finally {
    uploadingCount.value--;
  }
}

export function removeAttachment(id: string): void {
  const att = pendingAttachments.value.find((a) => a.id === id);
  if (att?.thumbUrl) URL.revokeObjectURL(att.thumbUrl);
  pendingAttachments.value = pendingAttachments.value.filter(
    (a) => a.id !== id,
  );
}

export function clearAttachments(): void {
  for (const att of pendingAttachments.value) {
    if (att.thumbUrl) URL.revokeObjectURL(att.thumbUrl);
  }
  pendingAttachments.value = [];
}

// ============================================================
// Chat seed reader (Ask Jarvis bridge, P1a)
// ============================================================

/**
 * Consume a pending chat seed written by DealActionBar (sessionStorage key
 * `chat_seed_${chatId}`, see DealActionBar.tsx). Returns the seed text and
 * removes the key so it is applied at most once. ChatInput calls this on
 * mount / active-chat switch to prefill the composer (never auto-send).
 */
export function consumeChatSeed(chatId: number): string | null {
  const key = `chat_seed_${chatId}`;
  try {
    const seed = sessionStorage.getItem(key);
    if (seed) sessionStorage.removeItem(key);
    return seed;
  } catch {
    return null;
  }
}

/**
 * The one Ask Jarvis bridge every tab uses. Creates a chat through the store
 * action (which adds it to the sidebar list AND sets activeChatId), seeds the
 * composer for that new chat, then navigates to /chat. Because createChat
 * already set activeChatId, ChatInput mounts on the correct chat and consumes
 * the seed. Callers must NOT use api.createChat + route('/chat?chat_id=') --
 * that path leaves activeChatId on the old chat, so the seed is read against
 * the wrong id and the new chat never appears (the P1a/P2 dead-link bug).
 */
export async function askJarvis(title: string, seedText: string): Promise<void> {
  const chatId = await createChat(title, 'pwa');
  if (chatId == null) throw new Error('Could not create a Jarvis chat.');
  try {
    sessionStorage.setItem(`chat_seed_${chatId}`, seedText);
  } catch {
    // ignore storage errors (private browsing, quota)
  }
  route('/chat');
}

/**
 * Create a context-specific chat and send the first message immediately.
 * Today notification detail uses this for its inline response composer: the
 * exact source message and Joseph's instruction travel together, so there is
 * no ambiguous "where should I reply?" handoff. WS outbox durability handles
 * a temporarily disconnected browser.
 */
export async function messageJarvis(title: string, message: string): Promise<void> {
  const chatId = await createChat(title, 'pwa');
  if (chatId == null) throw new Error('Could not create a Jarvis chat.');
  await sendMessage(message, [], chatId);
  route('/chat');
}

export function getReadyAttachments(): Array<{
  type: string;
  name: string;
  url?: string;
}> {
  return pendingAttachments.value
    .filter((a) => !a.uploading && a.url)
    .map((a) => ({
      type: a.type || 'file',
      name: a.name,
      url: a.url,
    }));
}

// ============================================================
// IDB hydration — paints last-known state BEFORE WS connects
// ============================================================

/**
 * Load persisted histories + in-flight stream snapshots from IndexedDB into
 * memory signals. Call at app boot, BEFORE connect() fires, so the UI paints
 * the last-known state instantly. After WS connects, `subscribeToChatWS`
 * sends a `subscribe` with the hydrated lastSeq map and the server replays
 * any chunks we missed while closed.
 *
 * Errors are swallowed: a cold start with a corrupt IDB should still work.
 */
export async function hydrateFromIdb(): Promise<void> {
  // Cache history is independent of stream recovery and never blocks it.
  void hydrateChatHistory(activeChatId.value);

  try {
    const snapshots = await loadStreamSnapshots();
    for (const snap of snapshots) {
      if (snap.chatId in chatStreamStates.value) continue;
      const persistedAt = Number.isFinite(snap.updatedAt) && snap.updatedAt > 0
        ? snap.updatedAt
        : null;
      // Seed as streaming so the router's seq-guard prevents replay of
      // already-applied chunks. WS replay will complete the stream.
      updateChatStream(snap.chatId, {
        streaming: true,
        content: snap.content,
        lastSeq: snap.lastSeq,
        startedAt: persistedAt,
        abortController: null,
        lastActivityAt: persistedAt,
      });
    }
  } catch (e) {
    console.warn('[chat-store] hydrate streams failed:', e);
  }
}

// ============================================================
// Durable lifecycle reconciliation
// ============================================================

const TURN_RECONCILE_ACTIVE_MS = 5_000;
const TURN_RECONCILE_IDLE_MS = 20_000;
let _turnReconcileTimer: ReturnType<typeof setTimeout> | null = null;
let _turnReconcileStopped = false;
let _turnReconcileInFlight: Promise<void> | null = null;
let _turnReconcileInFlightGeneration = 0;
let _turnReconcileRerunGeneration = 0;
let _turnReconcileGeneration = 0;

function timestampMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function adoptDurableRunningTurn(turn: DurableTurn, content = ''): void {
  const chatId = Number(turn.chat_id);
  const current = getChatStream(chatId);
  const heartbeatAt = timestampMs(turn.heartbeat_at) ?? Date.now();
  const activityAt = timestampMs(turn.activity_at) ?? heartbeatAt;
  const startedAt = timestampMs(turn.started_at) ?? current.startedAt ?? Date.now();
  const ownerChanged = current.turnId !== turn.turn_id;
  const nextContent = ownerChanged
    ? content
    : (content.length >= current.content.length ? content : current.content);
  updateChatStream(chatId, {
    streaming: true,
    turnId: turn.turn_id,
    provider: turn.provider || '',
    model: turn.model || '',
    phase: turn.phase || 'working',
    content: nextContent,
    startedAt,
    elapsedSeconds: Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
    elapsedReceivedAt: Date.now(),
    toolCalls: Number.isFinite(turn.tool_calls) ? turn.tool_calls : (ownerChanged ? 0 : current.toolCalls),
    lastTool: turn.last_tool || (ownerChanged ? '' : current.lastTool),
    activity: turn.activity || 'Working',
    lastActivityAt: activityAt,
    lastHeartbeatAt: heartbeatAt,
    interrupted: false,
    interruptionKind: null,
  });
  remoteStreamingChats.value = { ...remoteStreamingChats.value, [chatId]: true };
}

async function reconcileTerminalTurn(turn: DurableTurn, preserveActive = false): Promise<void> {
  const chatId = Number(turn.chat_id);
  const hasSuccessor = (): boolean => {
    const current = getChatStream(chatId);
    if (!current.streaming) return false;
    if (current.turnId && current.turnId !== turn.turn_id) return true;
    const completedAt = timestampMs(turn.completed_at);
    return completedAt != null && current.startedAt != null && current.startedAt > completedAt;
  };
  if (turn.state === 'completed') {
    // The backend commits chat history before setting a completed lifecycle.
    // Fetching that truth avoids duplicate bubbles after a reconnect or reload.
    // A locked private chat cannot be read until Joseph unlocks it, but its
    // history is already durable. Acknowledge its delivery record now so every
    // visible client does not retry the same forbidden history fetch forever;
    // the normal unlock flow will load the canonical history later.
    if (turn.history_persisted && !privateUnlocked.value && isPrivateChatId(chatId)) {
      await api.acknowledgeChatTurn(turn.turn_id);
      return;
    }
    if (turn.history_persisted && !(await loadChatHistory(chatId))) return;
    if (!turn.history_persisted && turn.result) {
      const existing = chatHistories.value[chatId] ?? [];
      const last = existing[existing.length - 1];
      if (last?.role !== 'assistant' || last.content !== turn.result) {
        appendAssistantMessage(chatId, turn.result);
      }
    }
    preserveActive = preserveActive || hasSuccessor();
    if (!turn.history_persisted) {
      if (!preserveActive) {
        clearChatStream(chatId);
        clearStreamIdb(chatId).catch(() => {});
        if (remoteStreamingChats.value[chatId]) {
          const next = { ...remoteStreamingChats.value };
          delete next[chatId];
          remoteStreamingChats.value = next;
        }
        drainQueue(chatId);
      }
      return;
    }
    if (!preserveActive) clearChatStream(chatId);
  } else {
    preserveActive = preserveActive || hasSuccessor();
    if (preserveActive) {
      await api.acknowledgeChatTurn(turn.turn_id);
      return;
    }
    updateChatStream(chatId, {
      streaming: false,
      turnId: turn.turn_id,
      provider: turn.provider || '',
      model: turn.model || '',
      phase: turn.phase || turn.state,
      activity: turn.error || turn.activity || 'Turn interrupted',
      lastHeartbeatAt: timestampMs(turn.heartbeat_at),
      lastActivityAt: timestampMs(turn.activity_at),
      interrupted: true,
      interruptionKind: turn.state === 'failed' ? 'error' : 'turn_lost',
    });
  }
  preserveActive = preserveActive || hasSuccessor();
  if (!preserveActive) clearStreamIdb(chatId).catch(() => {});
  if (!preserveActive && remoteStreamingChats.value[chatId]) {
    const next = { ...remoteStreamingChats.value };
    delete next[chatId];
    remoteStreamingChats.value = next;
  }
  await api.acknowledgeChatTurn(turn.turn_id);
  if (turn.state === 'completed' && !preserveActive && !hasSuccessor()) drainQueue(chatId);
}

/**
 * The gateway lists neither a running turn nor an uncollected result for this
 * chat: the run is over and another signed-in device already took its result.
 * That is not a failure by itself. History says whether an answer came back.
 */
async function settleUnlistedRun(chatId: number, stream: ChatStreamState): Promise<void> {
  // What this device held before asking: a message still waiting for its reply, and
  // the last answer that was already here. An answer only counts if it is a new one.
  const local = chatHistories.value[chatId] ?? [];
  const waiting = local[local.length - 1]?.role === 'user';
  const earlier = waiting ? [...local].reverse().find((message) => message.role === 'assistant') : undefined;
  // Until history can be read there is nothing true to say; the next pass decides.
  if (!(await loadChatHistory(chatId))) return;
  const answerId = stream.turnId ? await canonicalAnswerId(stream.turnId) : null;
  // A new run, or the history load itself, may have settled this one meanwhile.
  const current = getChatStream(chatId);
  if (current.turnId !== stream.turnId || current.startedAt !== stream.startedAt
    || current.streaming !== stream.streaming) return;
  const messages = chatHistories.value[chatId] ?? [];
  const last = messages[messages.length - 1];
  const sameAsEarlier = !!earlier && !!last
    && (earlier.id && last.id ? earlier.id === last.id : earlier.content === last.content);
  // Another reload may already have replaced what this device held, so the clock is
  // checked too: a reply saved before this run began is not its answer.
  const savedAt = last ? Date.parse(last.timestamp) : NaN;
  const olderThanRun = stream.startedAt != null && Number.isFinite(savedAt) && savedAt < stream.startedAt - 10_000;
  const answered = (last?.role === 'assistant' && !sameAsEarlier && !olderThanRun)
    || (!!answerId && messages.some((message) => message.role === 'assistant' && message.id === answerId));
  if (!answered) {
    updateChatStream(chatId, {
      streaming: false,
      interrupted: true,
      interruptionKind: 'turn_lost',
      phase: 'interrupted',
      activity: 'No reply came back for the last message.',
    });
    return;
  }
  clearChatStream(chatId);
  void clearStreamIdb(chatId).catch(() => {});
  if (remoteStreamingChats.value[chatId]) {
    const next = { ...remoteStreamingChats.value };
    delete next[chatId];
    remoteStreamingChats.value = next;
  }
  drainQueue(chatId);
}

export async function reconcileDurableTurns(): Promise<boolean> {
  try {
    const requestStartedAt = Date.now();
    const status = await api.getChatStatus();
    const activeIds = new Set<string>();
    const activeChats = new Set<number>();
    for (const turn of status.turns ?? []) {
      // A turn parked for capacity is still owned by the gateway and will run.
      if (turn.state === 'accepted' || turn.state === 'running' || turn.state === 'waiting_capacity') {
        activeIds.add(turn.turn_id);
        activeChats.add(Number(turn.chat_id));
        const live = status.streaming_chats?.[String(turn.chat_id)] ?? {};
        if (typeof live.turn_id === 'string' && live.turn_id !== turn.turn_id) continue;
        adoptDurableRunningTurn(
          turn,
          typeof live.content === 'string' ? live.content : '',
        );
      }
    }
    for (const turn of status.turns ?? []) {
      if (activeIds.has(turn.turn_id)) continue;
      await reconcileTerminalTurn(turn, activeChats.has(Number(turn.chat_id)));
    }
    for (const [chatIdText, stream] of Object.entries(chatStreamStates.value)) {
      const chatId = Number(chatIdText);
      // A dropped connection leaves a provisional notice that the turn list can settle too.
      const provisional = !stream.streaming && stream.interrupted && stream.interruptionKind === 'transport';
      if ((!stream.streaming && !provisional) || activeChats.has(chatId)) continue;
      // A reserved turn is live before it reaches the turn list.
      if (status.streaming_chats?.[chatIdText]?.streaming === true) continue;
      if (stream.startedAt && stream.startedAt > requestStartedAt) continue;
      if (stream.startedAt && Date.now() - stream.startedAt < 45_000) continue;
      await settleUnlistedRun(chatId, stream);
    }
    return true;
  } catch (error) {
    console.warn('[chat-store] durable turn reconciliation failed:', error);
    return false;
  }
}

async function runTurnReconcileLoop(): Promise<void> {
  if (_turnReconcileStopped || document.visibilityState !== 'visible') return;
  const generation = _turnReconcileGeneration;
  if (_turnReconcileInFlight) {
    if (_turnReconcileInFlightGeneration === generation) {
      await _turnReconcileInFlight;
      return;
    }
    if (_turnReconcileRerunGeneration === generation) return;
    _turnReconcileRerunGeneration = generation;
    const previous = _turnReconcileInFlight;
    await previous;
    if (
      !_turnReconcileStopped
      && generation === _turnReconcileGeneration
      && _turnReconcileRerunGeneration === generation
    ) {
      _turnReconcileRerunGeneration = 0;
      await runTurnReconcileLoop();
    }
    return;
  }
  _turnReconcileInFlightGeneration = generation;
  const task = (async () => {
    await reconcileDurableTurns();
    if (generation !== _turnReconcileGeneration || _turnReconcileStopped
      || document.visibilityState !== 'visible') return;
    if (chatsLoadError.value) await loadChats();
    else if (historyPages.value[activeChatId.value]?.error === 'Could not load messages. Please try again.'
      && !getChatStream(activeChatId.value).streaming
      && canReceiveChatWhileLocked(activeChatId.value)) {
      await loadChatHistory(activeChatId.value);
    }
    if (chats.value.length && Date.now() - lastMetadataReadAt >= 60_000) await refreshChatMetadata();
  })();
  _turnReconcileInFlight = task;
  try {
    await task;
  } finally {
    if (_turnReconcileInFlight === task) _turnReconcileInFlight = null;
  }
  if (_turnReconcileStopped || generation !== _turnReconcileGeneration) return;
  // A run left unsettled by a dropped connection is checked as often as a live one.
  const active = Object.values(chatStreamStates.value).some((stream) =>
    stream.streaming || stream.interrupted && stream.interruptionKind === 'transport');
  _turnReconcileTimer = setTimeout(
    () => { void runTurnReconcileLoop(); },
    active || !wsConnected.value ? TURN_RECONCILE_ACTIVE_MS : TURN_RECONCILE_IDLE_MS,
  );
}

export function startTurnReconciler(): () => void {
  _turnReconcileGeneration += 1;
  const generation = _turnReconcileGeneration;
  _turnReconcileStopped = false;
  const resume = () => {
    if (generation !== _turnReconcileGeneration) return;
    if (document.visibilityState !== 'visible') {
      if (_turnReconcileTimer) clearTimeout(_turnReconcileTimer);
      _turnReconcileTimer = null;
      return;
    }
    if (_turnReconcileTimer) clearTimeout(_turnReconcileTimer);
    _turnReconcileTimer = null;
    void runTurnReconcileLoop();
  };
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('online', resume);
  resume();
  return () => {
    if (generation !== _turnReconcileGeneration) return;
    _turnReconcileGeneration += 1;
    _turnReconcileStopped = true;
    if (_turnReconcileTimer) clearTimeout(_turnReconcileTimer);
    _turnReconcileTimer = null;
    document.removeEventListener('visibilitychange', resume);
    window.removeEventListener('online', resume);
  };
}

// ============================================================
// WS-driven chat sync: chunk routing, sync, replay subscribe
// ============================================================

let _wsChatCleanup: (() => void) | null = null;
let _wsConnectionCleanup: (() => void) | null = null;
let _wsPromptCleanup: (() => void) | null = null;

export function buildReplaySubscriptions(
  states: Record<number, { streaming?: boolean; interrupted?: boolean; lastSeq: number }>,
  chatList: Array<{ id: number; private?: boolean }>,
  unlocked: boolean,
): Array<{ chat_id: number; last_seq: number }> {
  const privacy = new Map(chatList.map((chat) => [chat.id, chat.private === true]));
  const subscriptions: Array<{ chat_id: number; last_seq: number }> = [];
  for (const [chatIdText, state] of Object.entries(states)) {
    const chatId = Number(chatIdText);
    if (!unlocked && privacy.get(chatId) !== false) continue;
    if (state.streaming || state.interrupted || state.lastSeq > 0) {
      subscriptions.push({ chat_id: chatId, last_seq: state.lastSeq });
    }
  }
  return subscriptions;
}

function sendReplaySubscriptions(): void {
  const subscriptions = buildReplaySubscriptions(
    chatStreamStates.value,
    chats.value,
    privateUnlocked.value,
  );
  if (subscriptions.length > 0) wsSendSubscribe(subscriptions);
}

let wasPrivateUnlocked = privateUnlocked.value;
effect(() => {
  const unlocked = privateUnlocked.value;
  if (unlocked && !wasPrivateUnlocked) {
    sendReplaySubscriptions();
    const active = activeChatId.value;
    if (isPrivateChatId(active)) void loadChatHistory(active);
  } else if (!unlocked && wasPrivateUnlocked) {
    const privateIds = new Set(
      chats.value.filter((chat) => chat.private).map((chat) => chat.id),
    );
    for (const chatId of privateIds) cancelQueuedChatMessages(chatId);
    remoteStreamingChats.value = Object.fromEntries(
      Object.entries(remoteStreamingChats.value)
        .filter(([chatId]) => !privateIds.has(Number(chatId))),
    );
    forceReconnect();
  }
  wasPrivateUnlocked = unlocked;
});

function markStreamingChatsInterrupted(): void {
  for (const [id, stream] of Object.entries(chatStreamStates.value)) {
    if (stream.streaming) updateChatStream(Number(id), {
      interrupted: true,
      interruptionKind: 'transport',
    });
  }
}

/**
 * Subscribe the chat module to persistent WS events.
 *
 * Stream chunks (`chunk`, `result`, `done`, `error`) with a `chat_id` are
 * routed through the chat-stream-router to their per-chat state. Meta
 * events are handled directly:
 *
 *   - `connected`         → sync remote-streaming state, subscribe for
 *                           replay of any chats with lastSeq > 0, refresh
 *                           histories.
 *   - `streaming_resume`  → adopt an in-flight server stream; seed seq.
 *   - `chat_updated`      → reload history for that chat (unless streaming).
 */
export function subscribeToChatWS(): () => void {
  if (_wsChatCleanup) _wsChatCleanup();
  if (_wsConnectionCleanup) _wsConnectionCleanup();

  let observedConnection = wsConnected.value;
  _wsConnectionCleanup = effect(() => {
    const connected = wsConnected.value;
    if (observedConnection && !connected) markStreamingChatsInterrupted();
    observedConnection = connected;
  });

  // Pending question/plan cards (PH-02): refresh for the open chat on every
  // (re)connect and tab switch; live changes arrive as WS events.
  if (_wsPromptCleanup) _wsPromptCleanup();
  _wsPromptCleanup = effect(() => {
    const chatId = activeChatId.value;
    void privateUnlocked.value; // unlocking a private tab reloads its cards
    if (wsConnected.value) void loadChatPrompts(chatId);
  });

  _wsChatCleanup = onMessage((msg) => {
    const t = msg.type as string;
    const eventChatId = (msg as Record<string, unknown>).chat_id;
    if (
      typeof eventChatId === 'number'
      && t !== 'chat_updated'
      && !canReceiveChatWhileLocked(eventChatId)
    ) {
      return;
    }

    if (t === 'prompt' || t === 'prompt_resolved') {
      handlePromptEvent(msg as Record<string, unknown>);
      return;
    }

    if (t === 'accepted') {
      const d = msg as Record<string, unknown>;
      const clientMessageId = typeof d.client_message_id === 'string'
        ? d.client_message_id
        : null;
      if (clientMessageId) {
        pendingQueue.value = pendingQueue.value.filter(
          (message) => message.id !== clientMessageId,
        );
      }
      const chatId = typeof d.chat_id === 'number' ? d.chat_id : null;
      const turnId = typeof d.turn_id === 'string' ? d.turn_id : null;
      if (chatId != null && turnId) {
        const current = getChatStream(chatId);
        if (current.streaming && !current.turnId) {
          updateChatStream(chatId, { turnId, phase: 'queued', activity: 'Accepted by gateway' });
        }
      }
      return;
    }

    if (t === 'session_event') {
      const d = msg as Record<string, unknown>;
      const chatId = d.chat_id as number | undefined;
      if (typeof chatId !== 'number') return;
      if (d.kind !== 'turn_lost' && d.kind !== 'restarted') return;
      const stream = getChatStream(chatId);
      const seq = typeof d.seq === 'number' ? d.seq : undefined;
      if (seq != null && seq <= stream.lastSeq) return;
      if (!stream.streaming) return;
      updateChatStream(chatId, {
        streaming: false,
        // The last tool line is not a reason; a following error frame replaces this.
        activity: 'The run was lost before it finished.',
        lastActivityAt: Date.now(),
        interrupted: true,
        interruptionKind: 'turn_lost',
        ...(seq != null ? { lastSeq: seq } : {}),
      });
      if (remoteStreamingChats.value[chatId]) {
        const next = { ...remoteStreamingChats.value };
        delete next[chatId];
        remoteStreamingChats.value = next;
      }
      return;
    }

    if (t === 'transport_ready') {
      const queuedChatIds = new Set(pendingQueue.value.map((message) => message.chatId));
      for (const chatId of queuedChatIds) {
        if (!privateUnlocked.value && isPrivateChatId(chatId)) continue;
        const stream = getChatStream(chatId);
        if (!stream.streaming && !stream.interrupted) drainQueue(chatId);
      }
      return;
    }

    // --- Live progress (tool activity / narration) for a streaming chat ---
    // Transient UI only: never enters stream content or history. Applied
    // solely while this client already tracks the chat as streaming, so a
    // replayed straggler can't resurrect cleared state after finalize.
    if (t === 'activity' || t === 'tool_use') {
      const d = msg as Record<string, unknown>;
      const chatId = d.chat_id as number | undefined;
      if (chatId == null || typeof chatId !== 'number') return;
      const current = getChatStream(chatId);
      if (!current.streaming) return;
      const turnId = typeof d.turn_id === 'string' ? d.turn_id : null;
      if (turnId && current.turnId && turnId !== current.turnId) return;
      const line =
        t === 'tool_use'
          ? [d.tool, d.detail].filter((v) => typeof v === 'string' && v).join(' · ')
          : d.kind === 'thinking'
            ? current.activity || 'Reasoning…'
            : typeof d.content === 'string'
              ? d.content
              : '';
      let progressBlocks = current.progressBlocks;
      let progress = current.progress;
      const kind = typeof d.kind === 'string' ? d.kind : 'note';
      const text = line.trim();
      if (t === 'activity' && ['note', 'summary', 'status'].includes(kind)
        && text && text !== 'Working…' && text !== 'Codex is reconnecting.') {
        // Codex sends growing snapshots for each commentary/summary item.
        // Replace the matching block instead of appending each snapshot.
        const blocks = [...progressBlocks];
        let match = -1;
        for (let i = blocks.length - 1; i >= 0; i--) {
          if (blocks[i].kind === kind
            && (text.startsWith(blocks[i].text) || blocks[i].text.startsWith(text))) {
            match = i;
            break;
          }
        }
        if (match >= 0) {
          if (text.length > blocks[match].text.length) blocks[match] = { kind, text };
        } else if (!blocks.some((block) => block.text === text)) {
          blocks.push({ kind, text });
        }
        // Progress is transient. Bound its memory while keeping the newest work visible.
        while (blocks.length > 1 && composeProgress(blocks).length > 16000) {
          blocks.shift();
        }
        progressBlocks = blocks;
        progress = composeProgress(blocks);
      }
      const hasElapsed = typeof d.elapsed_s === 'number' && Number.isFinite(d.elapsed_s) && d.elapsed_s >= 0;
      const activityAgeSeconds = typeof d.last_activity_age_s === 'number'
        && Number.isFinite(d.last_activity_age_s)
        && d.last_activity_age_s >= 0
        ? d.last_activity_age_s
        : 0;
      updateChatStream(chatId, {
        ...(turnId ? { turnId } : {}),
        ...(typeof d.provider === 'string' ? { provider: d.provider } : {}),
        ...(typeof d.phase === 'string' ? { phase: d.phase } : {}),
        ...(hasElapsed ? { elapsedSeconds: d.elapsed_s as number, elapsedReceivedAt: Date.now() } : {}),
        ...(typeof d.tool_calls === 'number' && Number.isFinite(d.tool_calls) && d.tool_calls >= 0
          ? { toolCalls: d.tool_calls }
          : {}),
        ...(typeof d.last_tool === 'string' ? { lastTool: d.last_tool.slice(0, 160) } : {}),
        ...(line ? { activity: line.slice(0, 4000) } : {}),
        progress,
        progressBlocks,
        lastActivityAt: Date.now() - activityAgeSeconds * 1000,
        lastHeartbeatAt: Date.now(),
        ...(typeof d.seq === 'number' && d.seq > getChatStream(chatId).lastSeq
          ? { lastSeq: d.seq }
          : {}),
      });
      return;
    }

    // --- Stream chunks routed by chat_id ---
    if (t === 'chunk' || t === 'result' || t === 'done' || t === 'error') {
      const d = msg as Record<string, unknown>;
      const chatId = d.chat_id as number | undefined;
      if (chatId == null || typeof chatId !== 'number') return;
      routeChunk({
        type: t as 'chunk' | 'result' | 'done' | 'error',
        chat_id: chatId,
        content: d.content as string | undefined,
        mode: d.mode as 'replace' | 'delta' | undefined,
        seq: typeof d.seq === 'number' ? d.seq : undefined,
        done: typeof d.done === 'boolean' ? d.done : undefined,
        turn_id: typeof d.turn_id === 'string' ? d.turn_id : undefined,
      });
      return;
    }

    if (t === 'chat_updated') {
      const chatId = (msg as Record<string, unknown>).chat_id as number;
      if (chatId == null) return;
      void refreshChatMetadata().then(() => {
        if (canReceiveChatWhileLocked(chatId) && !getChatStream(chatId).streaming) void loadChatHistory(chatId);
      });
      return;
    }

    if (t === 'connected') {
      const data = msg as Record<string, unknown>;
      const streamingChats = (data.streaming_chats ?? {}) as Record<string, boolean>;
      const parsed: Record<number, boolean> = {};
      for (const [k, v] of Object.entries(streamingChats)) {
        const chatId = Number(k);
        if (canReceiveChatWhileLocked(chatId)) parsed[chatId] = !!v;
      }
      remoteStreamingChats.value = parsed;

      // Reconcile hydrated streams against server truth. A chat we brought
      // back from IDB with streaming=true but which the server does NOT list
      // as streaming is an orphan — the stream finished while we were
      // closed, and the server's replay buffer may already be TTL-evicted.
      // Lock the last-known content into history and clear local state so
      // the UI doesn't sit on a spinner forever (audit S2).
      const orphanAppended = new Set<number>();
      for (const [cidStr, st] of Object.entries(chatStreamStates.value)) {
        const chatId = Number(cidStr);
        if (!st.streaming) continue;
        if (parsed[chatId]) {
          // A transport disconnect is not a turn failure. If the reconnected
          // gateway still owns this stream, clear the provisional interruption.
          if (st.interruptionKind === 'transport') updateChatStream(chatId, {
            interrupted: false,
            interruptionKind: null,
            lastActivityAt: Date.now(),
          });
          continue;
        }
        if (st.interruptionKind === 'transport') {
          // A restarted gateway has no in-memory ownership record for the old
          // turn. Keep the provisional interruption and Continue action until
          // replay proves success or emits an explicit terminal failure.
          updateChatStream(chatId, {
            streaming: false,
            // Gateway memory is a new replay epoch. Keeping the old watermark
            // would discard seq 1..N from a normally-typed recovery message.
            lastSeq: 0,
          });
          continue;
        }
        // Grace window: a stream we started locally moments ago may not yet be
        // reflected in the server's streaming set (message still being
        // dispatched to a cold-spawning daemon). Clearing it here would wipe the
        // optimistic "working" state and strand a new tab with no indicator and
        // no reply — the exact new-tab bug. A genuine orphan (finished while we
        // were disconnected) is always older than this; if this one really did
        // orphan, the next `connected` after the window clears it, and any
        // finished reply arrives via the `pending` re-delivery / history reload.
        if (st.startedAt && Date.now() - st.startedAt < 45000) continue;
        if (st.content) {
          appendAssistantMessage(chatId, st.content);
          orphanAppended.add(chatId);
        }
        clearChatStream(chatId);
        clearStreamIdb(chatId).catch(() => {});
      }

      // Drain any pendingQueue messages for chats that are not currently
      // streaming. This catches the orphan-drain bug: if the user queued a
      // message while the PWA-side `streaming` was true (e.g. mid-stream
      // disconnect), the orphan-clear above would silently drop the queue
      // because clearChatStream bypasses the setStreamFinalized → drainQueue
      // chain. Sweeping after the orphan-clear ensures any orphaned queue
      // is eventually delivered. Also handles disconnect/reconnect storms
      // where the normal drain path never fires for a chat.
      const queuedChatIds = new Set(pendingQueue.value.map((m) => m.chatId));
      for (const chatId of queuedChatIds) {
        if (!privateUnlocked.value && isPrivateChatId(chatId)) continue;
        const stream = getChatStream(chatId);
        if (!stream.streaming && !stream.interrupted) {
          drainQueue(chatId);
        }
      }

      // Subscribe for replay of remaining chats — server will only ack
      // chats it still has a buffer for; orphans are already reconciled
      // above so they never end up in this list.
      sendReplaySubscriptions();

      // Coalesced: bounds full-reload rate under reconnect storms while
      // guaranteeing a trailing reload (reconnect-storm guard above).
      reloadAllChatHistoriesCoalesced(orphanAppended);
      return;
    }

    if (t === 'streaming_resume') {
      const d = msg as Record<string, unknown>;
      const chatId = d.chat_id as number | undefined;
      const content = (d.content as string) || '';
      const seq = typeof d.seq === 'number' ? d.seq : 0;
      const turnId = typeof d.turn_id === 'string' ? d.turn_id : null;
      if (chatId == null) return;

      const current = getChatStream(chatId);
      // Keep the same owner, but replace a stale owner with server truth.
      if (current.streaming && (!turnId || current.turnId === turnId)) return;

      // Adopt as an in-flight server stream. Seed lastSeq from the
      // server's current seq so the follow-up `subscribe` replay
      // (triggered on `connected`) doesn't re-send already-reflected
      // chunks.
      updateChatStream(chatId, {
        streaming: true,
        turnId,
        provider: typeof d.provider === 'string' ? d.provider : '',
        phase: typeof d.phase === 'string' ? d.phase : 'working',
        content,
        startedAt: Date.now() - 5000,
        abortController: null,
        lastSeq: seq,
        toolCalls: 0,
        lastTool: '',
        activity: 'Working',
        lastActivityAt: Date.now(),
        lastHeartbeatAt: Date.now(),
      });
      remoteStreamingChats.value = { ...remoteStreamingChats.value, [chatId]: true };
      return;
    }
  });

  return () => {
    if (_wsChatCleanup) {
      _wsChatCleanup();
      _wsChatCleanup = null;
    }
    if (_wsConnectionCleanup) {
      _wsConnectionCleanup();
      _wsConnectionCleanup = null;
    }
  };
}
